// src/pages/HomePage.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Table2,
  Calculator,
  BarChart3,
  Activity,
  FileText,
  ChevronRight,
  RefreshCcw,
} from "lucide-react";

function Section({ title, eyebrow, children, subdued = false }) {
  return (
    <section className={subdued ? "bg-slate-950/60" : "bg-slate-950"}>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
        {(eyebrow || title) && (
          <div className="text-center">
            {eyebrow && (
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/90 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-sky-300/80 ring-1 ring-slate-700/70">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                {eyebrow}
              </div>
            )}
            {title && (
              <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-slate-50">
                {title}
              </h2>
            )}
          </div>
        )}
        <div className={title || eyebrow ? "mt-8" : ""}>{children}</div>
      </div>
    </section>
  );
}

function SportCard({ name, to, logo }) {
  return (
    <Link
      to={to}
      className="group relative flex flex-col rounded-2xl border border-slate-800/80 bg-slate-950/90 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.9)] transition-transform hover:-translate-y-1 hover:border-sky-400/70 hover:shadow-[0_26px_70px_rgba(56,189,248,0.65)]"
    >
      <div className="flex items-center gap-3">
        {logo ? (
          <div className="flex h-10 w-10 items-center justify-center rounded-xl">
            <img
              src={logo}
              alt={name}
              className="h-8 w-auto object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 ring-1 ring-slate-700/80 text-xs font-semibold text-sky-300">
            {name.slice(0, 3).toUpperCase()}
          </div>
        )}

        <div>
          <div className="text-sm font-semibold text-slate-50">{name}</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">
            DFS Tools &amp; Insights
          </div>
        </div>
      </div>

      <div className="mt-4 flex-1 text-xs text-slate-300">
        Slate projections, ownership, optimizer tools, and key data built
        specifically for {name} DFS.
      </div>

      <div className="mt-4 inline-flex items-center text-xs font-semibold text-sky-300 group-hover:text-sky-200">
        Open dashboard
        <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-2xl bg-gradient-to-t from-sky-500/10 via-sky-500/0 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="flex flex-col rounded-2xl border border-slate-800/80 bg-slate-950/90 p-5 shadow-inner transition-transform hover:-translate-y-1 hover:border-sky-400/70">
      <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/40">
        {icon}
      </div>
      <div className="text-sm font-semibold text-slate-50">{title}</div>
      <div className="mt-2 text-xs text-slate-300 leading-relaxed">
        {desc}
      </div>
    </div>
  );
}

function PreviewCard({ preview, setPreview }) {
  const nflRows = [
    {
      name: "Jonathan Taylor",
      team: "IND",
      pos: "RB",
      jersey: 28,
      salary: "$8,700",
      proj: "22.4",
      value: "3.0x",
      own: "24.3%",
      teamLogo: "/logos/nfl/ind.png",
    },
    {
      name: "Patrick Mahomes",
      team: "KC",
      pos: "QB",
      jersey: 15,
      salary: "$8,000",
      proj: "24.9",
      value: "3.1x",
      own: "18.7%",
      teamLogo: "/logos/nfl/kc.png",
    },
    {
      name: "Puka Nacua",
      team: "LAR",
      pos: "WR",
      jersey: 12,
      salary: "$7,200",
      proj: "17.1",
      value: "2.8x",
      own: "21.5%",
      teamLogo: "/logos/nfl/lar.png",
    },
  ];

  const nascarRows = [
    {
      driver: "Denny Hamlin",
      carNumber: 11,
      team: "JGR",
      salary: "$10,800",
      proj: "76.4",
      value: "5.4x",
      own: "42.1%",
      numberImg: "/logos/nascar/Cup/11.png",
    },
    {
      driver: "Kyle Larson",
      carNumber: 5,
      team: "Hendrick",
      salary: "$10,500",
      proj: "72.9",
      value: "5.2x",
      own: "31.8%",
      numberImg: "/logos/nascar/Cup/5.png",
    },
    {
      driver: "Ross Chastain",
      carNumber: 1,
      team: "Trackhouse",
      salary: "$9,000",
      proj: "61.3",
      value: "5.1x",
      own: "24.7%",
      numberImg: "/logos/nascar/Cup/1.png",
    },
  ];

  const mlbRows = [
    {
      name: "Ronald Acuña Jr.",
      team: "ATL",
      pos: "OF",
      jersey: 13,
      salary: "$6,000",
      proj: "13.4",
      value: "2.7x",
      own: "18.9%",
      teamLogo: "/logos/mlb/atl.png",
    },
    {
      name: "Shohei Ohtani",
      team: "LAD",
      pos: "UTIL",
      jersey: 17,
      salary: "$6,400",
      proj: "14.2",
      value: "2.6x",
      own: "24.5%",
      teamLogo: "/logos/mlb/lad.png",
    },
    {
      name: "Aaron Judge",
      team: "NYY",
      pos: "OF",
      jersey: 99,
      salary: "$6,200",
      proj: "13.9",
      value: "2.5x",
      own: "21.3%",
      teamLogo: "/logos/mlb/nyy.png",
    },
  ];

  const nbaRows = [
    {
      name: "Luka Dončić",
      team: "LAL",
      pos: "PG",
      jersey: 77,
      salary: "$11,200",
      proj: "59.8",
      value: "5.3x",
      own: "31.2%",
      teamLogo: "/logos/nba/lal.png",
    },
    {
      name: "Jayson Tatum",
      team: "BOS",
      pos: "SF",
      jersey: 0,
      salary: "$9,800",
      proj: "49.6",
      value: "5.1x",
      own: "24.8%",
      teamLogo: "/logos/nba/bos.png",
    },
    {
      name: "Nikola Jokić",
      team: "DEN",
      pos: "C",
      jersey: 15,
      salary: "$11,600",
      proj: "61.4",
      value: "5.0x",
      own: "28.5%",
      teamLogo: "/logos/nba/den.png",
    },
  ];

  let rows;
  if (preview === "NFL") rows = nflRows;
  else if (preview === "NASCAR") rows = nascarRows;
  else if (preview === "MLB") rows = mlbRows;
  else rows = nbaRows;

  const isNASCAR = preview === "NASCAR";
  const isPlayerSport = !isNASCAR;

  return (
    <div className="w-full max-w-md rounded-2xl border border-slate-800/80 bg-slate-950/95 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="inline-flex items-center gap-1 rounded-full bg-slate-900/90 px-1 py-1 ring-1 ring-slate-700/80">
          {["NFL", "NASCAR", "MLB", "NBA"].map((sport) => {
            const active = preview === sport;

            return (
              <button
                key={sport}
                type="button"
                onClick={() => setPreview(sport)}
                className={
                  "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold transition " +
                  (active
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-300 hover:text-slate-100")
                }
              >
                <span className="h-2 w-2 rounded-full bg-sky-200" />
                {sport} Preview
              </button>
            );
          })}
        </div>

        <div className="text-[11px] text-slate-400 sm:ml-auto">
          Sample view — full tools use live slate data.
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-slate-950/90 ring-1 ring-slate-800/80">
        <div className="grid grid-cols-[minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)] gap-2 border-b border-slate-800/80 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
          <div>{isNASCAR ? "Driver" : "Player"}</div>
          <div>Team</div>
          <div>Proj / Val</div>
          <div>Own%</div>
        </div>

        <div className="divide-y divide-slate-800/80">
          {rows.map((row, idx) =>
            isPlayerSport ? (
              <div
                key={idx}
                className="grid grid-cols-[minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)] items-center gap-2 px-3 py-2.5 text-xs"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg">
                    {row.teamLogo && (
                      <img
                        src={row.teamLogo}
                        alt={row.team}
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </div>

                  <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-semibold text-slate-300">
                        {row.pos}
                      </span>
                      {row.jersey != null && (
                        <span className="text-[10px] text-slate-500">
                          #{row.jersey}
                        </span>
                      )}
                    </div>

                    <div className="text-[11px] font-semibold text-slate-50">
                      {row.name}
                    </div>
                  </div>
                </div>

                <div className="text-[11px] text-slate-300">{row.team}</div>

                <div className="flex flex-col text-[11px] text-slate-200">
                  <span>{row.proj} pts</span>
                  <span className="text-[10px] text-slate-400">
                    {row.value}
                  </span>
                </div>

                <div className="flex items-center justify-end">
                  <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] font-semibold text-sky-300 ring-1 ring-sky-500/50">
                    {row.own}
                  </span>
                </div>
              </div>
            ) : (
              <div
                key={idx}
                className="grid grid-cols-[minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)] items-center gap-2 px-3 py-2.5 text-xs"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg">
                    {row.numberImg && (
                      <img
                        src={row.numberImg}
                        alt={`#${row.carNumber}`}
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </div>

                  <div className="flex flex-col">
                    <div className="text-[11px] font-semibold text-slate-50">
                      {row.driver}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      Car #{row.carNumber}
                    </div>
                  </div>
                </div>

                <div className="text-[11px] text-slate-300">{row.team}</div>

                <div className="flex flex-col text-[11px] text-slate-200">
                  <span>{row.proj} pts</span>
                  <span className="text-[10px] text-slate-400">
                    {row.value}
                  </span>
                </div>

                <div className="flex items-center justify-end">
                  <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] font-semibold text-sky-300 ring-1 ring-sky-500/50">
                    {row.own}
                  </span>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <ul className="mt-4 space-y-1.5 text-[11px] text-slate-300">
        <li className="flex items-center gap-2">
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-300">
            ✓
          </span>
          Projections, value, and ownership in one table.
        </li>
        <li className="flex items-center gap-2">
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-300">
            ✓
          </span>
          Team &amp; driver logos wired into the data.
        </li>
      </ul>
    </div>
  );
}

export default function HomePage() {
  const [preview, setPreview] = useState("NFL");

  const sports = [
    { name: "NASCAR", to: "/nascar", logo: "/logos/nascar.png" },
    { name: "NFL", to: "/nfl", logo: "/logos/nfl.png" },
    { name: "MLB", to: "/mlb", logo: "/logos/mlb.png" },
    { name: "NBA", to: "/nba", logo: "/logos/nba.png" },
  ];

  const features = [
    {
      icon: <Table2 className="h-5 w-5" />,
      title: "Cheat Sheets",
      desc: "Sortable player and driver pools with projections, salary, value, optimal rates, leverage, and filters built for real slate decisions.",
    },
    {
      icon: <Calculator className="h-5 w-5" />,
      title: "Lineup Optimizers",
      desc: "DK and FD optimizers with locks, groups, exposure controls, CSV exports, and late swap tools available for NFL, MLB, and NBA.",
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: "Projections",
      desc: "Slate-ready projections, value, rates, and core metrics designed to support lineup building across NFL, NBA, MLB, and NASCAR.",
    },
    {
      icon: <Activity className="h-5 w-5" />,
      title: "Range of Outcomes",
      desc: "Player and driver outcome distributions showing ceiling paths, floor risk, boom rates, and how often each option can pay off.",
    },
    {
      icon: <RefreshCcw className="h-5 w-5" />,
      title: "Simulations",
      desc: "Simulated race, game, and player outcomes to uncover ceiling paths, range of outcomes, and fair values.",
    },
    {
      icon: <FileText className="h-5 w-5" />,
      title: "Data Hub",
      desc: "Slate-specific stats, sim outputs, matchup data, and DFS/betting angles across NFL, NBA, MLB, and NASCAR.",
    },
  ];

  const quickLinks = [
    {
      name: "NASCAR",
      to: "/nascar",
      logo: "/logos/nascar.png",
      desc: "Driver projections, ownership, sims, dominator paths, and lineup optimizers.",
    },
    {
      name: "NFL",
      to: "/nfl",
      logo: "/logos/nfl.png",
      desc: "Classic and showdown projections, ownership, stacks, optimizer, and late swap.",
    },
    {
      name: "MLB",
      to: "/mlb",
      logo: "/logos/mlb.png",
      desc: "Pitcher and hitter projections, stacks, ownership, optimizer, and late swap.",
    },
    {
      name: "NBA",
      to: "/nba",
      logo: "/logos/nba.png",
      desc: "Player projections, value, ownership, optimizer, and late swap tools.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* HERO + PREVIEW */}
      <section className="relative overflow-hidden border-b border-slate-800/80 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="pointer-events-none absolute inset-0 opacity-50">
          <div className="absolute -top-32 left-0 h-64 w-64 rounded-full bg-sky-500/25 blur-3xl" />
          <div className="absolute bottom-[-140px] right-[-60px] h-80 w-80 rounded-full bg-sky-400/20 blur-3xl" />
        </div>

        <div className="relative mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 sm:py-12 lg:flex-row lg:items-center lg:justify-between lg:px-4 lg:py-16">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-950/80 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-sky-100 shadow-[0_0_25px_rgba(56,189,248,0.5)]">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
              Cpenn DFS &amp; Betting Data
            </div>

            <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white">
              DFS tools built for{" "}
              <span className="bg-gradient-to-r from-sky-400 via-sky-300 to-slate-100 bg-clip-text text-transparent">
                every slate
              </span>
              .
            </h1>

            <p className="mt-4 text-sm sm:text-base text-slate-200/90 max-w-lg">
              Projections, ownership, sims, optimizers, and clean slate data
              built to help DFS players make sharper lineup decisions across
              NFL, NBA, MLB, and NASCAR.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs sm:text-sm">
              <Link
                to="/nfl"
                className="inline-flex items-center rounded-full bg-sky-500 px-4 py-2 text-xs font-semibold text-slate-950 shadow hover:bg-sky-400"
              >
                Jump into NFL
              </Link>
              <Link
                to="/nascar"
                className="inline-flex items-center rounded-full border border-slate-600 bg-slate-950/80 px-4 py-2 text-xs font-semibold text-slate-100 hover:border-sky-400"
              >
                Open NASCAR tools
              </Link>
              <Link
                to="/mlb"
                className="inline-flex items-center rounded-full border border-slate-600 bg-slate-950/80 px-4 py-2 text-xs font-semibold text-slate-100 hover:border-sky-400"
              >
                Open MLB tools
              </Link>
              <Link
                to="/nba"
                className="inline-flex items-center rounded-full border border-slate-600 bg-slate-950/80 px-4 py-2 text-xs font-semibold text-slate-100 hover:border-sky-400"
              >
                Open NBA tools
              </Link>
            </div>

            <div className="mt-5">
              <ul className="space-y-1.5 text-[11px] text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-300">
                    ✓
                  </span>
                  DK + FD focused, updated every slate.
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-300">
                    ✓
                  </span>
                  Projections, sims, optimizers, and data tools all in one
                  place.
                </li>
              </ul>

              <div className="mt-4 text-xs text-slate-300">
                Ready for the next slate?{" "}
                <Link
                  to="/pricing"
                  className="font-semibold text-sky-300 underline underline-offset-4 transition hover:text-sky-200"
                >
                  Start with a free 7-day trial.
                </Link>
              </div>
            </div>
          </div>

          <div className="w-full lg:max-w-md">
            <PreviewCard preview={preview} setPreview={setPreview} />
          </div>
        </div>
      </section>

      {/* SPORTS GRID */}
      <Section title="Supported Sports" eyebrow="Dashboards">
        <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
          {sports.map((s) => (
            <SportCard key={s.name} {...s} />
          ))}
        </div>
      </Section>

      {/* FEATURES */}
      <Section title="Tools & Features" eyebrow="What you get" subdued>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {features.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </Section>

      {/* CTA / QUICK ACCESS BAND */}
      <Section subdued>
        <div className="relative overflow-hidden rounded-3xl border border-slate-800/80 bg-slate-950/90 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.9)] md:p-8">
          <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-cyan-400/10 blur-3xl" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/90 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-sky-300/80 ring-1 ring-slate-700/70">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                Build the next slate
              </div>

              <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                Jump straight into the tools.
              </h2>

              <p className="mt-2 max-w-xl text-sm text-slate-300">
                Open projections, compare ownership, check ranges, then build and adjust lineups with the tools for the sport you’re playing.
              </p>
            </div>

            <Link
              to="/pricing"
              className="inline-flex w-fit items-center rounded-full bg-sky-500 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow hover:bg-sky-400"
            >
              Start free trial
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Link>
          </div>

          <div className="relative mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {quickLinks.map((sport) => (
              <Link
                key={sport.name}
                to={sport.to}
                className="group rounded-2xl border border-slate-800/80 bg-slate-900/60 p-4 transition hover:-translate-y-0.5 hover:border-sky-400/70 hover:bg-slate-900"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-950/80 ring-1 ring-slate-700/80">
                      <img
                        src={sport.logo}
                        alt={sport.name}
                        className="h-7 w-auto object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>

                    <div>
                      <div className="text-sm font-bold text-slate-50">
                        {sport.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">
                        DFS tools
                      </div>
                    </div>
                  </div>

                  <ChevronRight className="h-4 w-4 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-sky-300" />
                </div>

                <p className="mt-3 text-xs leading-relaxed text-slate-300">
                  {sport.desc}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}